import { z } from "zod";

import { Layout, LayoutItem, PanelType } from "@/shared/state/visualization";
import { LayoutType } from "@/shared/state/visualization/schema";

const LayoutRequirementSchema = z.object({
  topicMessagePath: z.string(),
  dataType: z.string(),
});

const LayoutRuleSchema = z.object({
  id: z.string(),
  requirements: z.array(LayoutRequirementSchema),
});

const LayoutPanelSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(PanelType),
  rules: z.array(z.string()),
});

const LayoutItemSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(LayoutType),
  relativeSize: z.number(),
  isResizing: z.boolean(),
});

const LayoutSchema: z.ZodType<Layout> = z.object({
  id: z.string(),
  axis: z.enum(["x", "y"]),
  relativeSize: z.number(),
  children: z.array(z.lazy(() => z.union([LayoutSchema, LayoutItemSchema]))),
});

export const LayoutDefinitionSchema = z
  .object({
    layout: LayoutSchema,
    panels: z.record(z.string(), LayoutPanelSchema),
    rules: z.array(LayoutRuleSchema),
  })
  .superRefine((def, ctx) => {
    // 1. Verify all rules are referenced by panels
    const usedRules = new Set(
      Object.values(def.panels).flatMap((panel) => panel.rules),
    );

    def.rules.forEach((rule) => {
      if (!usedRules.has(rule.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Rule ${rule.id} is not referenced by any panel`,
          path: ["rules", rule.id],
        });
      }
    });

    // 2. Verify all layout items correspond to panels
    const panelIds = new Set(Object.keys(def.panels));
    const validateLayoutItems = (layout: Layout | LayoutItem) => {
      if ("children" in layout) {
        layout.children.forEach(validateLayoutItems);
      } else if (!panelIds.has(layout.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Layout item ${layout.id} does not correspond to any panel`,
          path: ["layout", layout.id],
        });
      }
    };
    validateLayoutItems(def.layout);
  });
